import {
    ApolloClient,
    InMemoryCache,
    ApolloLink,
    Observable
  } from "@apollo/client";
import { createUploadLink } from 'apollo-upload-client'
import { setContext } from '@apollo/client/link/context';
import { onError } from "@apollo/client/link/error";
import { getEnvConfig } from 'config/envConfig'

const envConfig = getEnvConfig()
export const hotelxNodeUrl = envConfig.hotelxNodeUrl
export const RedirectUrl = envConfig.redirectUrl

export const UploadLink = createUploadLink({
  uri: hotelxNodeUrl,
  credentials: 'include',
})
const ONLINE_OPERATIONS = new Set([
  'OnlineHotelInfo',
  'OnlineHotelRoomSearch',
  'OnlinePackagePriceListing',
  'OnlineSegmentListing',
  'OnlineCurrentTaxDetails',
  'OnlineCheckoutInfo',
  'OnlineCheckoutPackagePriceListing',
  'OnlineComputeTaxBooking',
  'OnlineComputeTaxArrByHotel',
  'OnlineDiscountByPromoCode',
  'OnlineIsBillplz',
  'OnlineCheckoutBookingInsert',
  'OnlineCheckoutPayment',
])
let onlineToken: {
  hostname: string
  accessToken: string
  expiresAt: number
} | null = null
let onlineBootstrap: Promise<string> | null = null

const getOnlineToken = async (): Promise<string> => {
  const hostname = window.location.hostname.toLowerCase().replace(/\.$/, '')
  const tokenIsCurrent = onlineToken
    && onlineToken.hostname === hostname
    && onlineToken.expiresAt > Date.now() + 15000
  if (tokenIsCurrent) {
    return onlineToken.accessToken
  }
  if (!onlineBootstrap) {
    onlineBootstrap = (async () => {
      let lastError: any
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await fetch(hotelxNodeUrl!, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              query: `query OnlineBrowsingBootstrap($Hostname: String!) {
                OnlineBrowsingBootstrap(Hostname: $Hostname) {
                  accessToken
                  expiresIn
                }
              }`,
              variables: { Hostname: hostname },
            }),
          })
          if (!response.ok) {
            throw new Error('Online browsing is temporarily unavailable.')
          }
          const result = await response.json()
          if (result.errors?.length) {
            const message = result.errors[0].message || 'Hotel is unavailable.'
            throw new Error(message)
          }
          const session = result.data?.OnlineBrowsingBootstrap
          if (!session?.accessToken || !session.expiresIn) {
            throw new Error('Hotel is unavailable.')
          }
          onlineToken = {
            hostname,
            accessToken: session.accessToken,
            expiresAt: Date.now() + session.expiresIn * 1000,
          }
          return session.accessToken
        } catch (error: any) {
          lastError = error
          if (/hotel is unavailable/i.test(error?.message) || attempt > 0) {
            throw error
          }
          await new Promise((resolve) => setTimeout(resolve, 300))
        }
      }
      throw lastError
    })().finally(() => {
      onlineBootstrap = null
    })
  }
  return onlineBootstrap
}

export const OnlineBrowsingLink = new ApolloLink((operation, forward) => {
  if (!ONLINE_OPERATIONS.has(operation.operationName)) return forward(operation)
  return new Observable((observer) => {
    let subscription: any
    let cancelled = false
    getOnlineToken().then((token) => {
      if (cancelled) return
      operation.setContext(({ headers = {} }) => ({
        headers: { ...headers, authorization: `Bearer ${token}` },
      }))
      subscription = forward(operation).subscribe(observer)
    }).catch((error) => observer.error(error))
    return () => {
      cancelled = true
      if (subscription) subscription.unsubscribe()
    }
  })
})
export const RequestLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      let handle: any
      Promise.resolve(operation)
        .then((operation) => {
          if (ONLINE_OPERATIONS.has(operation.operationName)) {
            return
          }
          const accessToken = JSON.parse(localStorage.getItem('GuestInfo'))?.accessToken
          if (accessToken) {
            operation.setContext({
              headers: {
                authorization: `bearer ${accessToken}`,
              },
            })
          }
        })
        .then(() => {
          handle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          })
        })
        .catch(observer.error.bind(observer))

      return () => {
        if (handle) handle.unsubscribe()
      }
    })
)
export const AuthLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = JSON.parse(localStorage.getItem('GuestLoginInfo'))
  ?.accessToken;
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: `bearer ${token}`,
    }
  }
});
const errorLink = onError(({
  graphQLErrors,
  networkError,
  operation,
  forward,
}) => {
  const isOnlineOperation = ONLINE_OPERATIONS.has(operation.operationName)
  const onlineRetryableFailure = isOnlineOperation && (
    !!networkError
    || graphQLErrors?.some(({ message }) => (
      /invalid or expired|temporarily unavailable/i.test(message)
    ))
  )
  if (onlineRetryableFailure && !operation.getContext().onlineBrowsingRetried) {
    const sessionExpired = graphQLErrors?.some(({ message }) => (
      /invalid or expired/i.test(message)
    ))
    if (sessionExpired) onlineToken = null
    operation.setContext({ onlineBrowsingRetried: true })
    return new Observable((observer) => {
      let subscription: any
      getOnlineToken().then((token) => {
        operation.setContext(({ headers = {} }) => ({
          headers: { ...headers, authorization: `Bearer ${token}` },
        }))
        subscription = forward(operation).subscribe(observer)
      }).catch((error) => observer.error(error))
      return () => {
        if (subscription) subscription.unsubscribe()
      }
    })
  }
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      )
    })
  }

  if (networkError) {
    console.log(`[Network error]: ${networkError}`)
  }
});
export const Client = new ApolloClient({
  uri: hotelxNodeUrl,
  cache: new InMemoryCache(),
  link: ApolloLink.from([
    AuthLink,
    OnlineBrowsingLink,
    RequestLink,
    errorLink,
    UploadLink as any,
  ]),
})
